import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ReelList = () => {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [reelToDelete, setReelToDelete] = useState(null);
  const [reelToEdit, setReelToEdit] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', isActive: true });
  const [updateLoading, setUpdateLoading] = useState(false);
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

  useEffect(() => {
    fetchReels();
  }, []);

  const fetchReels = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/reels`);
      
      const transformedReels = response.data.reels.map(reel => ({
        ...reel,
        isActive: reel.isActive !== undefined ? reel.isActive : true,
        videoUrl: reel.video
      }));
      
      setReels(transformedReels);
      setError(null);
    } catch (err) {
      console.error('Error fetching reels:', err);
      setError(err.response?.data?.error || 'Failed to fetch reels');
      setReels([]);
      toast.error('Failed to load reels');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (reel) => {
    setReelToDelete(reel);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!reelToDelete) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/reels/${reelToDelete.id}`);
      
      setReels(reels.filter(reel => reel.id !== reelToDelete.id));
      toast.success('Reel deleted successfully');
    } catch (err) {
      console.error('Error deleting reel:', err);
      toast.error(err.response?.data?.error || 'Failed to delete reel');
    } finally {
      setDeleteModalOpen(false);
      setReelToDelete(null);
    }
  };

  const openEditModal = (reel) => {
    setReelToEdit(reel);
    setEditForm({
      title: reel.title,
      isActive: reel.isActive
    });
    setEditModalOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!reelToEdit) return;

    // Validation
    if (!editForm.title.trim()) {
      toast.error('Title cannot be empty');
      return;
    }

    setUpdateLoading(true);

    try {
      const response = await axios.put(
        `${API_BASE_URL}/reels/${reelToEdit.id}`,
        {
          title: editForm.title.trim(),
          isActive: editForm.isActive
        }
      );

      // Update the reel in the list
      setReels(reels.map(reel => 
        reel.id === reelToEdit.id 
          ? { 
              ...reel, 
              title: response.data.reel.title,
              isActive: response.data.reel.isActive,
              updatedAt: response.data.reel.updatedAt
            }
          : reel
      ));

      toast.success(response.data.message || 'Reel updated successfully');
      setEditModalOpen(false);
      setReelToEdit(null);
    } catch (err) {
      console.error('Error updating reel:', err);
      toast.error(err.response?.data?.error || 'Failed to update reel');
    } finally {
      setUpdateLoading(false);
    }
  };

  const toggleActiveStatus = async (reel) => {
    const newStatus = !reel.isActive;
    
    try {
      const response = await axios.put(
        `${API_BASE_URL}/reels/${reel.id}`,
        {
          isActive: newStatus
        }
      );

      setReels(reels.map(r => 
        r.id === reel.id 
          ? { ...r, isActive: response.data.reel.isActive }
          : r
      ));

      toast.success(`Reel ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      console.error('Error toggling reel status:', err);
      toast.error(err.response?.data?.error || 'Failed to update reel status');
    }
  };

  if (loading) {
    return (
      <div className="mb-8">
        <h3 className="text-lg font-medium mb-3">Current Reels</h3>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <h3 className="text-lg font-medium mb-3">Current Reels</h3>
        <div className="p-4 bg-red-100 text-red-700 rounded-md">
          {error}
          <button 
            onClick={fetchReels} 
            className="ml-2 text-blue-600 hover:text-blue-800 underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (reels.length === 0) {
    return (
      <div className="mb-8">
        <h3 className="text-lg font-medium mb-3">Current Reels</h3>
        <div className="p-4 bg-gray-100 text-gray-700 rounded-md">
          No reels found. Create your first reel!
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium">Current Reels ({reels.length})</h3>
        <button
          onClick={fetchReels}
          className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 rounded"
        >
          Refresh
        </button>
      </div>
      
      <div className="space-y-4">
        {reels.map((reel) => (
          <div key={reel.id} className="border rounded-lg p-4 flex items-start hover:shadow-md transition-shadow">
            <div className="w-1/4 mr-4 bg-gray-100 rounded-lg flex items-center justify-center h-32 overflow-hidden">
              <video 
                src={reel.videoUrl} 
                className="w-full h-full object-cover"
                muted
                loop
                playsInline
                onMouseEnter={(e) => e.target.play()}
                onMouseLeave={(e) => e.target.pause()}
              />
            </div>
            
            <div className="flex-1">
              <h4 className="font-medium text-lg mb-1">{reel.title}</h4>
              <p className="text-gray-500 text-xs mb-2 break-all line-clamp-1" title={reel.videoUrl}>
                {reel.videoUrl}
              </p>
              
              <div className="flex items-center space-x-4 text-xs text-gray-500">
                {reel.createdAt && (
                  <span>Created: {new Date(reel.createdAt).toLocaleDateString()}</span>
                )}
                {reel.updatedAt && reel.updatedAt !== reel.createdAt && (
                  <span>Updated: {new Date(reel.updatedAt).toLocaleDateString()}</span>
                )}
              </div>
              
              <div className="flex items-center mt-2">
                <button
                  onClick={() => toggleActiveStatus(reel)}
                  className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                >
                  <span
                    className={`inline-block w-3 h-3 rounded-full ${
                      reel.isActive ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  ></span>
                  <span className="text-sm font-medium">
                    {reel.isActive ? 'Active' : 'Inactive'}
                  </span>
                </button>
              </div>
            </div>
            
            <div className="flex flex-col space-y-2">
              <button
                onClick={() => openEditModal(reel)}
                className="px-3 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-600 rounded"
              >
                Edit
              </button>
              <button
                onClick={() => confirmDelete(reel)}
                className="px-3 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-600 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editModalOpen && reelToEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Edit Reel</h3>
              <button
                onClick={() => setEditModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                disabled={updateLoading}
              >
                &times;
              </button>
            </div>
            
            <form onSubmit={handleUpdate} className="space-y-4">
              {/* Title Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Reel title"
                  required
                  disabled={updateLoading}
                />
              </div>

              {/* Active Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                  id="edit-reel-active"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={updateLoading}
                />
                <label htmlFor="edit-reel-active" className="ml-2 block text-sm text-gray-700">
                  Active
                </label>
              </div>

              {/* Video Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Video
                </label>
                <video 
                  src={reelToEdit.videoUrl} 
                  controls 
                  className="w-full h-40 object-cover rounded-lg"
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setEditModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  disabled={updateLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center disabled:opacity-50"
                  disabled={updateLoading}
                >
                  {updateLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </>
                  ) : (
                    'Update Reel'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && reelToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Confirm Deletion</h3>
              <button
                onClick={() => setDeleteModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            
            <div className="mb-4">
              <p className="mb-2">
                Are you sure you want to delete the reel <strong>"{reelToDelete?.title}"</strong>?
              </p>
              <p className="text-sm text-red-600">
                ⚠️ This will permanently delete the video from cloud storage and cannot be undone.
              </p>
            </div>
            
            {reelToDelete?.videoUrl && (
              <div className="mb-4">
                <video 
                  src={reelToDelete.videoUrl} 
                  className="w-full h-32 object-cover rounded-lg"
                  muted
                />
              </div>
            )}
            
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setDeleteModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReelList;